import dynamic from 'next/dynamic'
import { type FC, type PropsWithChildren, useRef, useState } from 'react'

import { type PSAKeys } from '@/constants/psaBanner'
import { useClickOutside } from '@/hooks/useClickOutside'

import { ErrorBoundary } from '../error-boundary'
import { GlobalMessages } from '../global-messages'
import { usePSABanner } from '../psa-banner/usePSABanner'
import { SearchBar } from './search-bar'
import { SearchBarContainer } from './search-bar/styled'
import { Container, GlobalHeaderContainer, LogoWrapper, Wrapper } from './styled'
import { Tools } from './tools'

const DynamicLogo = dynamic(() => import('./Logo').then((mod) => mod.Logo), { ssr: false })

export type GlobalHeaderProps = PropsWithChildren & {
  /**
   * Will show the search bar in the header. Defaults to false.
   */
  showSearchBar: boolean
  psaCampaign?: PSAKeys
}

export const GlobalHeader: FC<GlobalHeaderProps> = ({
  psaCampaign,
  showSearchBar = false,
  children,
}) => {
  const [isMobileHeaderExpanded, toggleMobileHeader] = useState<boolean>(false)
  const globalHeader = useRef<HTMLElement>(null)

  const { isVisible: isPSAVisible } = usePSABanner(psaCampaign)

  const handleClickOut = () => {
    toggleMobileHeader(false)
  }

  useClickOutside(globalHeader, () => handleClickOut)

  return (
    <>
      <Container
        ref={globalHeader}
        id="global-header"
        hasChildren={!!children}
        isPSABannerVisible={isPSAVisible}
      >
        <GlobalHeaderContainer>
          <Wrapper>
            <LogoWrapper data-testid="global-header-logo">
              <DynamicLogo />
            </LogoWrapper>

            <ErrorBoundary fingerprintId="GlobalHeader">
              {showSearchBar && (
                <SearchBarContainer showSearchBar={showSearchBar} data-testid="global-search-bar">
                  <SearchBar
                    isMobileHeaderExpanded={isMobileHeaderExpanded}
                    toggleMobileHeader={toggleMobileHeader}
                  />
                </SearchBarContainer>
              )}
            </ErrorBoundary>

            <Tools showPostAdCTA={showSearchBar} />
          </Wrapper>

          {children}
        </GlobalHeaderContainer>
      </Container>

      <GlobalMessages />
    </>
  )
}
